import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { InformationCircleIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';

const MarketingReviewModal = ({ requestId, onClose, onValidated, title = 'Revue Marketing', submit, approveNote = 'La demande sera transmise à la directrice marketing pour validation finale.' }) => {
  const [action, setAction] = useState(null); // 'approve' | 'reject'
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documents, setDocuments] = useState([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);

  useEffect(() => {
    api.documents.getByRequest(requestId)
      .then(res => setDocuments(res.data?.data || []))
      .catch(() => {})
      .finally(() => setIsLoadingDocs(false));
  }, [requestId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!action) {
      setError('Veuillez choisir une action (approuver ou refuser)');
      return;
    }
    if (action === 'reject' && !comment.trim()) {
      setError('Un motif de refus est requis');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const call = submit || api.marketingValidation.review;
      const response = await call(requestId, { action, comment });
      toast.success(action === 'approve' ? 'Demande approuvée' : 'Demande refusée');
      if (onValidated) onValidated(response.data.data);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de la revue marketing';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentLabel = (doc) => {
    if (doc.type === 'decharge') return 'Décharge';
    if (doc.type === 'attribution') return "Note d'attribution";
    if (doc.type === 'noteInterne') return 'Note interne';
    return doc.filename;
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="modal-btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="marketing-review-form"
            disabled={loading || !action}
            className={action === 'reject' ? 'modal-btn-danger' : 'modal-btn-success'}
          >
            {loading ? 'Envoi…' : action === 'reject' ? 'Confirmer le refus' : action === 'approve' ? 'Confirmer l\'approbation' : 'Confirmer'}
          </button>
        </>
      }
    >
      <form id="marketing-review-form" onSubmit={handleSubmit}>
        <p className="text-sm text-gray-500 mb-4">
          Vérifiez les documents ci-dessous avant d'approuver ou de refuser la demande.
        </p>

        {/* Documents list */}
        <div className="mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Documents à vérifier</p>
                      {isLoadingDocs ? (
                        <p className="text-sm text-gray-400">Chargement des documents…</p>
                      ) : documents.length > 0 ? (
                        <ul className="space-y-2">
                          {documents.map(doc => (
                            <li key={doc._id} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
                              <div className="flex items-center gap-2">
                                <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-800">{getDocumentLabel(doc)}</span>
                              </div>
                              <a
                                href={`/${doc.path}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                Ouvrir
                              </a>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-400 italic">Aucun document disponible</p>
                      )}
                    </div>

                    {/* Action selector */}
                    <div className="mb-4">
                      <p className="text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">Décision</p>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setAction('approve')}
                          className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ${
                            action === 'approve'
                              ? 'bg-green-600 text-white border-green-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-green-50 hover:border-green-400'
                          }`}
                        >
                          Approuver
                        </button>
                        <button
                          type="button"
                          onClick={() => setAction('reject')}
                          className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors ${
                            action === 'reject'
                              ? 'bg-red-600 text-white border-red-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-red-50 hover:border-red-400'
                          }`}
                        >
                          Refuser
                        </button>
                      </div>
                    </div>

                    {/* Comment */}
                    <div className="mb-4">
                      <label htmlFor="review-comment" className="block text-sm font-medium text-gray-700">
                        {action === 'reject' ? 'Motif de refus *' : 'Commentaire (optionnel)'}
                      </label>
                      <textarea
                        id="review-comment"
                        rows="3"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md"
                        placeholder={action === 'reject' ? 'Expliquez la raison du refus…' : 'Ajouter un commentaire…'}
                        disabled={loading}
                      />
                    </div>

                    {error && (
                      <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md text-sm">
                        {error}
                      </div>
                    )}

                    {action === 'approve' && (
                      <div className="bg-blue-50 p-3 rounded-md mt-2">
                        <div className="flex">
                          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                          <p className="ml-3 text-sm text-blue-700">
                            <strong>Note :</strong> {approveNote}
                          </p>
                        </div>
                      </div>
        )}
      </form>
    </Modal>
  );
};

export default MarketingReviewModal;
